import React, { useState } from "react";
import { toast } from "react-toastify";
import ComposeMessage from "./ComposeMessage";
import { useStatusBanner } from "../shared/StatusBannerContext";
import { useCompactMode } from "../../context/CompactModeContext";
import FolderDropdown from "./FolderDropdown";
import "./MessagePreview.css";
import "../shared/StatusBanner.css";
import "../../styles/buttons.css";

export default function MessagePreview({
  message,
  highlightQuery,
  onClose,
  onReply,
  folders = [],                 // ✅ passed from parent (Messages page)
  onMoveFolder,                 // ✅ optional: parent can update state/backend
}) {
  const [showReply, setShowReply] = useState(false);
  const banner = useStatusBanner();          // ✅ call once
  const { compactMode } = useCompactMode();

  if (!message) return null;

  const highlightText = (text = "", query = "") => {
    if (!query) return text;
    const parts = String(text).split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? <mark key={i}>{part}</mark> : part
    );
  };

  const handleArchive = () => {
    onMoveFolder?.(message, "archive");
    banner?.setStatusMessage?.("Message moved to Archive");
    onClose?.();
  };

  const handleSpam = () => {
    onMoveFolder?.(message, "spam");
    banner?.setStatusMessage?.("Message flagged as spam");
    onClose?.();
  };

  const handleForward = () => {
    setShowReply(true);

    banner?.setStatusMessage?.("Forwarding message");
  };

  const handleKey = (e, action) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
    }
  };

  // ✅ If parent provided folders, use them. Otherwise show an empty dropdown safely.
  const safeFolders = Array.isArray(folders) ? folders : [];

  return (
    <>
      {!showReply ? (
        <div
          className={`message-preview-card ${compactMode ? "compact" : ""}`}
          role="region"
          aria-label={`Preview of message from ${message.fromUserId?.name}`}
        >
          <div className="message-header">
            <div className="header-top">
              <h4 tabIndex="0">{highlightText(message.subject, highlightQuery)}</h4>

              <button
                type="button"
                className="close-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose?.();
                }}
                aria-label="Close message preview"
              >
                ×
              </button>
            </div>

            <p className="sender-line">
              <strong>From:</strong> {highlightText(message.fromUserId?.name, highlightQuery)}
            </p>

            <p className="meta-line">
              <strong>Time:</strong> {message.time || "—"}
            </p>
          </div>

          <div className="message-body" tabIndex="0">
            <p>{highlightText(message.body, highlightQuery)}</p>
          </div>

          <div className="message-actions" role="group" aria-label="Message actions">
            <button
              className="btn btn--reply"
              onClick={() => {
                // you can either open inline reply OR let parent handle reply
                onReply?.(message);
                // or: setShowReply(true);
              }}
              onKeyDown={(e) => handleKey(e, () => onReply?.(message))}
              aria-label="Reply"
            >
              Reply
            </button>

            <button
              className="btn btn--forward"
              onClick={handleForward}
              onKeyDown={(e) => handleKey(e, handleForward)}
              aria-label="Forward"
            >
              Forward
            </button>

            <button
              className="btn btn--archive btn--success"
              onClick={handleArchive}
              onKeyDown={(e) => handleKey(e, handleArchive)}
              aria-label="Archive"
            >
              Archive
            </button>

            <button
              className="btn btn--spam btn--danger"
              onClick={handleSpam}
              onKeyDown={(e) => handleKey(e, handleSpam)}
              aria-label="Mark as spam"
            >
              Spam
            </button>

          </div>
        </div>
      ) : (
        <ComposeMessage
          initialTo={message.fromUserId?.email || ""}
          initialSubject={`Fwd: ${message.subject}`}
          initialBody={`\n\n--- Original Message ---\nFrom: ${message.fromUserId?.name || "Unknown Sender"
            }\nSubject: ${message.subject}\n\n${message.body}`}
          onClose={() => setShowReply(false)}
        />
      )}
    </>
  );
}